import React, { useCallback, useState, useMemo } from 'react'

import { FormattedMessage, useIntl } from 'react-intl'
import { Button, Divider, Typography, Row, Col, Modal } from 'antd'

import { useLocale } from 'stores/UserStore'
import { HighwayDiscussionStore } from 'stores/HighwayDiscussionStore'
import { Stat } from 'components/Stat/Stat'
import { Date } from 'components/Date/Date'
import { PolygonList } from 'components/PolygonList/PolygonList'
import { CreateForm } from './CreateForm'
import { EditContentForm } from 'components/Product/EditContentForm'
import { DeleteButton } from 'components/DeleteButton/DeleteButton'
import { Preview } from 'components/SpecialProducts/Preview'
import { Spacer } from 'components/VerticalSpacer/Spacer'
import {
    putHighwayDiscussion,
    cloneHighwayDiscussion,
    publishHighwayDiscussion,
    deleteHighwayDiscussion,
    sendEmail,
} from 'services/HighwayDiscussions'
import { sendErrorNotification, useSendNotification } from 'utils/Notifications'
import messages from 'services/intl/messageDefinitions'
import { API_SUCCESS_CODES } from 'utils/Constants'
import { getDrawerDetailsWidth } from 'utils/screen'
import { usePolygons } from 'hooks/polygons'
import { useHighwayDiscussions } from 'hooks/highwayDiscussions'
import { ScreenshotGenerator } from './ScreenshotGenerator'
import { clearImages, getImages } from './indexedDB'
import { uploadImage } from './helpers'
import {
    useSetIDIndex,
    useSetImageUrlsAndGroupIDs,
    useSetShowScreenshotModal,
    useShowScreenshotModal,
    useDoneGeneratingImages,
    useReinitialize,
} from 'stores/HighwayDiscussionImageStore'
import { usePreviewEmail } from 'hooks/highwayDiscussions'

export const Details = ({ item, closeAndReload, closeAndOpenCloned, PreviewButton = null, viewWidth }) => {
    const intl = useIntl()

    const [product, setProduct] = useState(item)
    const [cloneError, setCloneError] = useState(false)
    const [deleteModalVisible, setDeleteModalVisible] = useState(false)
    const { Title } = Typography
    const editingProductSetup = HighwayDiscussionStore.useEditingProductSetup()
    const setEditingProductSetup = HighwayDiscussionStore.useSetEditingProductSetup()
    const editingProductContent = HighwayDiscussionStore.useEditingProductContent()
    const setEditingProductContent = HighwayDiscussionStore.useSetEditingProductContent()
    const locale = useLocale()
    const sendNotification = useSendNotification()
    const detailsColWidth = getDrawerDetailsWidth(viewWidth)
    const polygons = usePolygons()
    const { mutate } = useHighwayDiscussions()
    const showScreenshotModal = useShowScreenshotModal()
    const setShowScreenshotModal = useSetShowScreenshotModal()
    const setIDIndex = useSetIDIndex()
    const setImageUrlsAndGroupIDs = useSetImageUrlsAndGroupIDs()
    const doneGeneratingImages = useDoneGeneratingImages()
    const { previewEmail } = usePreviewEmail(item.id)
    const computedGroupIDs = previewEmail?.groups?.map((group) => group.id)
    const reinitialize = useReinitialize()

    const updateDiscussion = useCallback(async (data, locale) => {
        const response = await putHighwayDiscussion(data, locale)
        if (API_SUCCESS_CODES.includes(response.status)) {
            sendNotification(intl.formatMessage({ ...messages.highwayDiscussionSaved }, { name: data.name }))
            mutate()
            setProduct(response.data)
        } else {
            sendErrorNotification(intl.formatMessage({ ...messages.highwayDiscussionSaveError }, { name: data.name }))
        }
    }, [])

    const cloneSelectedDiscussion = useCallback(async (item, locale) => {
        const response = await cloneHighwayDiscussion(item.id, locale)
        if (API_SUCCESS_CODES.includes(response.status)) {
            closeAndOpenCloned(response.data.id)
        } else {
            if (response.data && response.data.message === 'Polygons are assigned to draft product') {
                setCloneError(true)
            } else {
                sendErrorNotification(intl.formatMessage({ ...messages.productCloneError }))
            }
        }
    }, [])

    const publish = useCallback(async (item) => {
        await publishHighwayDiscussion(item.id, locale)
        mutate()
    }, [])

    // Render maps in a modal and take screenshots
    const handlePreview = useCallback(async () => {
        if (computedGroupIDs?.length) {
            reinitialize({ groupIDs: computedGroupIDs })
        } else {
            sendErrorNotification(intl.formatMessage({ ...messages.noGroupIds }))
        }
    }, [computedGroupIDs, intl, reinitialize])

    const handleSendEmail = async () => {
        try {
            const images = await getImages();

            const promises = images.map(async (image) => {
                try {
                    // Upload images to Cloudinary
                    return await uploadImage(image.image);
                } catch (error) {
                    console.error(`Error uploading image with groupID ${image.groupID}:`, error);
                    throw error; // Re-throw to be handled by Promise.all
                }
            });

            const uploadedImages = await Promise.all(promises);

            const imageUrlsAndGroupsIDs = images.map((image, index) => {
                return {
                    url: uploadedImages[index].url,
                    groupId: image.groupID,
                };
            });

            setImageUrlsAndGroupIDs(imageUrlsAndGroupsIDs);

            const response = await sendEmail(item.id, imageUrlsAndGroupsIDs);

            if (API_SUCCESS_CODES.includes(response.status)) {
                sendNotification(intl.formatMessage({ ...messages.emailSent }));
                await clearImages();
                setShowScreenshotModal(false);
            } else {
                sendErrorNotification(intl.formatMessage({ ...messages.emailError }));
                console.error("Email sending failed with status:", response.status);
            }
        } catch (error) {
            sendErrorNotification(intl.formatMessage({ ...messages.imageUploadFailed }));
            console.error("Error in handleSendEmail:", error);
        }
    }

    const deleteDiscussion = useCallback(async (id, locale) => {
        await deleteHighwayDiscussion(id, locale)
        sendNotification(intl.formatMessage({ ...messages.highwayDiscussionDeleted }, { name: item.name }))
        setDeleteModalVisible(false)
        closeAndReload()
    }, [])

    // Updates the various fields in the product
    // details should be in the format: [{key: <field to be changed> value: <updated values>}, {...}]
    const update = (details) => {
        const data = { ...item }
        for (let d in details) {
            const detail = details[d]
            data[detail.key] = detail.value
        }

        updateDiscussion(data, locale)
    }

    const apiPut = (data) => {
        updateDiscussion(data, locale)
    }

    const toggleEdit = () => {
        setEditingProductSetup(!editingProductSetup)
    }

    const toggleContentEdit = () => {
        setEditingProductContent(!editingProductContent)
    }

    const detailsView = (
        <div style={styles.detailsBody}>
            <Title level={2} style={styles.title}>
                {item.name}
            </Title>
            <div style={styles.header}>
                <div>{item.username}</div>
                <Date label={'lastModified'} time={true} value={item.lastModifiedDateTime} />
            </div>
            <div style={styles.detailsContainer}>
                <Divider orientation="left" plain>
                    <FormattedMessage {...messages.details} />
                </Divider>
                <Row gutter={8}>
                    <Col style={styles.detailCol} span={detailsColWidth}>
                        <Stat label={'issueDateTime'} value={item.issueDateTime} type={'date'} time={true} />
                    </Col>
                    <Col style={styles.detailCol} span={detailsColWidth}>
                        <Stat label={'expiryDateTime'} value={item.expiryDateTime} type={'date'} time={true} />
                    </Col>
                </Row>
                <PolygonList polygonList={item.polygons} colour={item.colour} polygons={polygons} />
            </div>
            {item.status === 'draft' && (
                <>
                    <Row style={styles.buttonContainer}>
                        <Button onClick={toggleEdit}>
                            <FormattedMessage {...messages.editSetup} />
                        </Button>
                        <Button
                            onClick={toggleContentEdit}
                            disabled={!item.userID}
                            title={intl.formatMessage({ ...messages.missingForecaster })}
                            data-test="editContentButton"
                        >
                            <FormattedMessage {...messages.editContent} />
                        </Button>
                        <Button onClick={() => cloneSelectedDiscussion(product, locale)}>
                            <FormattedMessage {...messages.cloneToDraft} />
                        </Button>
                        <Button type="primary" onClick={() => publish(product)}>
                            <FormattedMessage {...messages.publish} />
                        </Button>
                    </Row>
                    <DeleteButton
                        setDeleteModalVisible={setDeleteModalVisible}
                        buttonText={'deleteHighwayDiscussion'}
                    />
                </>
            )}
            {(item.status === 'completed' || item.status === 'live') && (
                <>
                    <Row style={styles.buttonContainer}>
                        <Button onClick={() => cloneSelectedDiscussion(item, locale)}>
                            <FormattedMessage {...messages.cloneToDraft} />
                        </Button>
                        {item.status === 'live' && (
                            <>
                                <Button type="primary" onClick={handlePreview}>
                                    <FormattedMessage {...messages.previewEmail} />
                                </Button>
                                <Modal
                                    visible={showScreenshotModal}
                                    onCancel={async () => {
                                        setShowScreenshotModal(false)
                                        setIDIndex(null)
                                        await clearImages()
                                    }}
                                    footer={null}
                                    title={
                                        <div style={styles.previewHeader}>
                                            <span>
                                                <FormattedMessage {...messages.previewEmail} />
                                            </span>
                                            <Button
                                                type="primary"
                                                size="small"
                                                disabled={!doneGeneratingImages}
                                                onClick={handleSendEmail}
                                            >
                                                <FormattedMessage {...messages.sendEmail} />
                                            </Button>
                                        </div>
                                    }
                                    width={800}
                                    height={600}
                                >
                                    <ScreenshotGenerator productId={product.id} />
                                </Modal>
                            </>
                        )}
                    </Row>
                    <DeleteButton
                        setDeleteModalVisible={setDeleteModalVisible}
                        buttonText={'deleteHighwayDiscussion'}
                    />
                </>
            )}

            <Spacer />
            {['completed', 'live'].includes(item.status) && <Preview item={item} />}
        </div>
    )

    const editSetupView = (
        <>
            <CreateForm
                toggleEdit={toggleEdit}
                editing={editingProductSetup}
                update={update}
                initialValues={product}
                autoUpdate={true}
                closeAndReload={closeAndReload}
                setDeleteModalVisible={setDeleteModalVisible}
            />
        </>
    )

    const editContentView = (
        <EditContentForm toggleContentEdit={toggleContentEdit} item={product} update={update} apiPut={apiPut} />
    )

    // Show details by default; otherwise, show editSetup or editContent
    let contentView = detailsView

    if (editingProductSetup) {
        contentView = editSetupView
    }

    if (editingProductContent) {
        contentView = editContentView
    }

    return (
        <>
            {contentView}
            <Modal
                title={<FormattedMessage {...messages.warning} />}
                visible={deleteModalVisible}
                onOk={() => deleteDiscussion(item.id, locale)}
                onCancel={() => setDeleteModalVisible(false)}
                okText={<FormattedMessage {...messages.yesConfirmDeleteHighwayDiscussion} />}
                cancelText={<FormattedMessage {...messages.cancel} />}
            >
                <p>
                    <FormattedMessage {...messages.deleteHighwayDiscussionConfirmation} values={{ name: item.name }} />
                </p>
            </Modal>
            <Modal
                title={<FormattedMessage {...messages.clonePolygonError} />}
                visible={cloneError}
                onCancel={() => setCloneError(false)}
                okText={<FormattedMessage {...messages.ok} />}
                footer={
                    <Button type="primary" onClick={() => setCloneError(false)}>
                        <FormattedMessage {...messages.ok} />
                    </Button>
                }
            >
                <p>
                    <FormattedMessage {...messages.productCloneErrorPolygons} />
                </p>
            </Modal>
        </>
    )
}

const styles = {
    detailsBody: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
    },
    title: {
        fontWeight: '400',
        flex: '0 1 auto',
    },
    header: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--s-1)',
        justifyContent: 'space-between',
        flex: '0 1 auto',
    },
    detailsContainer: {
        marginTop: 'var(--s0)',
    },
    detailCol: {
        marginBottom: 'var(--s0)',
    },
    buttonContainer: {
        marginTop: '25px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'var(--s0)',
    },
    previewHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginRight: '1.5em',
    },
}
